import { useEffect, useRef, useState } from 'react';

/**
 * L'animation de la colonne de droite des écrans publics (charte V3).
 *
 * Elle remplace l'image du bateau, qui était un décor : celle-ci montre ce que le
 * portail fait, des montants qui se remplissent et une facture qui arrive.
 *
 * **Trois précautions, et aucune n'est optionnelle.**
 *
 * `lottie-web` pèse près de 250 Ko : il est chargé **à la demande** par un `import()`
 * dynamique, donc il ne rentre pas dans le bundle de la page de connexion. L'écran
 * s'affiche entier sans lui, et l'animation arrive ensuite en fondu.
 *
 * **`prefers-reduced-motion` coupe tout**, et rien n'est chargé du tout : ce n'est pas
 * une animation figée sur sa première image, c'est l'absence d'animation. Un objet
 * animé arrêté au milieu de sa course se lit comme un défaut, ce que la charte V3 dit
 * explicitement du même cas sur l'anneau d'attente.
 *
 * **Le JSON ne va chercher aucune police**, et c'est une modification faite en le
 * copiant : la source déclarait Instrument Sans par une URL Google Fonts, que
 * `lottie-web` transforme en `<link>` vers un domaine tiers. La police est chargée
 * localement par `@fontsource-variable/instrument-sans`, donc l'animation lit celle
 * du document.
 */
const LoginAnimation = () => {
    const boite = useRef<HTMLDivElement>(null);
    const [pret, setPret] = useState(false);

    useEffect(() => {
        // Lu à l'exécution et non par un media query CSS : ce qu'on coupe ici est le
        // TÉLÉCHARGEMENT du lecteur, pas seulement le mouvement.
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
            return;
        }

        let animation: { destroy: () => void } | null = null;
        let abandonne = false;

        void (async () => {
            try {
                const [{ default: lottie }, { default: donnees }] = await Promise.all([
                    import('lottie-web/build/player/lottie_light'),
                    import('../../assets/lottie-connexion.json'),
                ]);

                // Le composant a pu être démonté pendant le chargement : monter
                // l'animation dans un noeud détaché laisserait une boucle qui tourne
                // sur un écran que personne ne regarde.
                if (abandonne || !boite.current) {
                    return;
                }

                animation = lottie.loadAnimation({
                    container: boite.current,
                    renderer: 'svg',
                    loop: true,
                    autoplay: true,
                    animationData: donnees,
                });
                setPret(true);
            } catch {
                // Le lecteur n'a pas pu être chargé : la colonne reste sur son aplat
                // bleu, ce qui est un fond et non un trou. Rien à dire au lecteur,
                // qui vient se connecter et n'a rien perdu.
            }
        })();

        return () => {
            abandonne = true;
            animation?.destroy();
        };
    }, []);

    return (
        <div
            ref={boite}
            className={`login-animation${pret ? ' login-animation--pret' : ''}`}
            /* Décorative au sens strict : elle ne porte aucune information que le
               formulaire ne porte pas. Elle sort donc de l'arbre d'accessibilité,
               comme `aria-hidden` sur `.l-visuel` dans la maquette. */
            aria-hidden="true"
        />
    );
};

export default LoginAnimation;
